// frontend/src/pages/Dashboard.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../axiosConfig";
import "../styles/Dashboard.css";
import { useTranslation } from "react-i18next";
import KpiCard from "../pages/KpiCard.js";

import {
  FaRegCalendarAlt,
  FaRegClock,
  FaChevronDown,
  FaCheck,
  FaEuroSign,
  FaFileInvoice,
  FaClock,
  FaCube,
  FaArrowDown,
  FaArrowUp,
  FaExclamationTriangle,
  FaChartLine,
} from "react-icons/fa";

import {
  AlertCircle,
  Truck,
  Users,
  Container,
  Building2,
  Factory,
  ShieldCheck,
  Package,
  LayoutDashboard,
} from "lucide-react";

function fmtISO(d) {
  return d.toISOString().slice(0, 10);
}

function computeRange(periodId) {
  const now = new Date();

  if (periodId === "daily") {
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return { from: fmtISO(from), to: fmtISO(to) };
  }

  if (periodId === "weekly") {
    const day = now.getDay(); // 0..6 (Sun..Sat)
    const diffToMonday = (day + 6) % 7;
    const from = new Date(now);
    from.setDate(now.getDate() - diffToMonday);
    const to = new Date(from);
    to.setDate(from.getDate() + 6);
    return { from: fmtISO(from), to: fmtISO(to) };
  }

  if (periodId === "monthly") {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: fmtISO(from), to: fmtISO(to) };
  }

  if (periodId === "yearly") {
    const from = new Date(now.getFullYear(), 0, 1);
    const to = new Date(now.getFullYear(), 11, 31);
    return { from: fmtISO(from), to: fmtISO(to) };
  }

  const from = new Date(now);
  from.setDate(now.getDate() - 29);
  return { from: fmtISO(from), to: fmtISO(now) };
}

function normalizeNumberSpaces(s) {
  return String(s || "")
    .replace(/\u202f/g, " ") // narrow no-break space
    .replace(/\u00a0/g, " "); // no-break space
}

function countDecimalPlacesFromRaw(v) {
  if (v == null || v === "") return 0;

  const s = String(v).trim();

  if (/^-?\d+\.\d+$/.test(s)) {
    return s.split(".")[1].length;
  }

  if (/^-?\d+,\d+$/.test(s)) {
    return s.split(",")[1].length;
  }

  const match = s.toLowerCase().match(/e-(\d+)$/);
  if (match) return Number(match[1]) || 0;

  return 0;
}

function formatFullNumber(v, locale = "fr-FR", fallbackDecimals = 0) {
  const n = Number(String(v ?? 0).replace(",", "."));
  if (!Number.isFinite(n)) return "0";

  const rawDecimals = countDecimalPlacesFromRaw(v);
  const decimals = Math.min(20, Math.max(rawDecimals, fallbackDecimals));

  return normalizeNumberSpaces(
    n.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  );
}

function formatMoney(v, locale = "fr-FR") {
  const n = Number(String(v ?? 0).replace(",", "."));
  if (!Number.isFinite(n)) return "0,00";

  return normalizeNumberSpaces(
    n.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function formatMoneyHeadline(v, locale = "fr-FR") {
  return `€${formatMoney(v, locale)}`;
}

function formatPercentFull(v, locale = "fr-FR") {
  return formatFullNumber(v, locale, 0);
}

function formatCount(v, locale = "fr-FR") {
  return formatFullNumber(v, locale, 0);
}

function formatRangeLabel(from, to, locale = "pt-PT") {
  const fmt = (s) => {
    if (!s) return "";
    const d = new Date(`${s}T00:00:00`);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  };

  return {
    fromLabel: fmt(from),
    toLabel: fmt(to),
  };
}

function computePreviousRange(periodId, from, to) {
  const fromDate = new Date(`${from}T00:00:00`);
  const toDate = new Date(`${to}T00:00:00`);

  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return { from: "", to: "" };
  }

  if (periodId === "monthly") {
    const prevFrom = new Date(fromDate.getFullYear(), fromDate.getMonth() - 1, 1);
    const prevTo = new Date(fromDate.getFullYear(), fromDate.getMonth(), 0);
    return { from: fmtISO(prevFrom), to: fmtISO(prevTo) };
  }

  if (periodId === "yearly") {
    const prevFrom = new Date(fromDate.getFullYear() - 1, 0, 1);
    const prevTo = new Date(fromDate.getFullYear() - 1, 11, 31);
    return { from: fmtISO(prevFrom), to: fmtISO(prevTo) };
  }

  const diffDays =
    Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000)) + 1;

  const prevTo = new Date(fromDate);
  prevTo.setDate(prevTo.getDate() - 1);

  const prevFrom = new Date(prevTo);
  prevFrom.setDate(prevFrom.getDate() - (diffDays - 1));

  return { from: fmtISO(prevFrom), to: fmtISO(prevTo) };
}

function daysBetweenInclusive(from, to) {
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
}

function getRevenueBucket(periodId, from, to) {
  if (periodId === "daily" || periodId === "weekly" || periodId === "monthly") {
    return "day";
  }

  if (periodId === "yearly") {
    return "month";
  }

  const totalDays = daysBetweenInclusive(from, to);

  if (totalDays > 180) return "month";
  if (totalDays > 45) return "week";
  return "day";
}

function getWeekStart(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  const day = d.getDay(); // 0..6
  const diffToMonday = (day + 6) % 7;
  d.setDate(d.getDate() - diffToMonday);
  return d;
}

function formatBucketLabel(bucket, locale = "pt-PT") {
  if (bucket.type === "month") {
    return bucket.date.toLocaleDateString(locale, { month: "short" });
  }

  if (bucket.type === "week") {
    const end = new Date(bucket.date);
    end.setDate(end.getDate() + 6);

    const a = bucket.date.toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
    const b = end.toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
    return `${a}–${b}`;
  }

  return bucket.date.toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
}

function aggregateRevenueSeries(rows = [], bucket = "day", locale = "pt-PT") {
  const safeRows = Array.isArray(rows) ? rows : [];
  if (bucket === "day") {
    return safeRows.map((r) => ({
      date: r.date,
      value: Number(r?.value || 0),
      label: (() => {
        const d = new Date(`${r.date}T00:00:00`);
        if (Number.isNaN(d.getTime())) return String(r.date || "");
        return d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
      })(),
    }));
  }

  const map = new Map();

  for (const row of safeRows) {
    const rawDate = row?.date;
    const value = Number(row?.value || 0);
    if (!rawDate) continue;

    let key = rawDate;
    let bucketDate = null;
    let type = bucket;

    if (bucket === "month") {
      const d = new Date(`${rawDate}T00:00:00`);
      if (Number.isNaN(d.getTime())) continue;
      bucketDate = new Date(d.getFullYear(), d.getMonth(), 1);
      key = `${bucketDate.getFullYear()}-${String(bucketDate.getMonth() + 1).padStart(2, "0")}`;
    } else if (bucket === "week") {
      bucketDate = getWeekStart(rawDate);
      key = bucketDate.toISOString().slice(0, 10);
    }

    if (!map.has(key)) {
      map.set(key, {
        type,
        date: bucketDate,
        value: 0,
      });
    }

    map.get(key).value += value;
  }

  return Array.from(map.values())
    .sort((a, b) => a.date - b.date)
    .map((item) => ({
      date: item.date.toISOString().slice(0, 10),
      value: Number(item.value || 0),
      label: formatBucketLabel(item, locale),
    }));
}

// ✅ Europe/Lisbon "today" as YYYY-MM-DD (frontend-safe)
function todayYMDLisbon() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Lisbon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}

function getDashboardCompanyScope() {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("loadevoToken") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("loadevoToken") ||
    "";

  if (!token) return "no-token";

  try {
    const payloadPart = token.split(".")[1];

    if (payloadPart) {
      const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(window.atob(normalized));

      const companyId =
        payload?.companyId ||
        payload?.company_id ||
        payload?.CompanyId ||
        payload?.company ||
        null;

      const userId =
        payload?.user_id ||
        payload?.id ||
        payload?.sub ||
        null;

      if (companyId) return `company-${companyId}`;
      if (userId) return `user-${userId}`;
    }
  } catch (err) {
    console.warn("[Dashboard] could not decode auth token for cache scope", err);
  }

  // fallback: still separates different login sessions better than global cache
  return `token-${token.slice(-24)}`;
}

function buildDashboardCacheKey({ from, to, locale, companyScope }) {
  return `loadevo.dashboard.summary.${companyScope}.${from}.${to}.${locale}`;
}

function readDashboardCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    return {
      etag: parsed.etag || "",
      data: parsed.data || null,
      savedAt: parsed.savedAt || null,
    };
  } catch (err) {
    console.warn("[Dashboard] failed to read local dashboard cache", err);
    return null;
  }
}

function writeDashboardCache(key, payload) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        etag: payload?.etag || "",
        data: payload?.data || null,
        savedAt: Date.now(),
      })
    );
  } catch (err) {
    console.warn("[Dashboard] failed to persist local dashboard cache", err);
  }
}

// removed unused MiniLine component

/**
 * Shopify-like line chart:
 * - Left padding for Y labels
 * - Soft grid
 * - X labels (few ticks)
 * - Smooth line + filled area
 * - Keeps your hover tooltip + vertical line
 */
function BigLineChart({
  series = [],
  valueKey = "value",
  compareSeries = null,
  compareValueKey = "value",
  fromLabel = "",
  toLabel = "",
  compareFromLabel = "",
  compareToLabel = "",
  variant = "default",
  headlineValue = 0,
  headlineDeltaPct = 0,
  t,
  locale = "pt-PT",
}) {
  const isShopifyLight = variant === "shopifyLight";

  const w = 940;
  const h = isShopifyLight ? 352 : variant === "trend" ? 240 : 170;
  
  const padL = isShopifyLight ? 48 : 48;
  const padR = isShopifyLight ? 18 : 14;
  const padT = isShopifyLight ? 14 : 14;
  const padB = isShopifyLight ? 40 : variant === "trend" ? 48 : 30;

  const wrapRef = React.useRef(null);
  const svgRef = React.useRef(null);
  const tipRef = React.useRef(null);

  const [hoverIdx, setHoverIdx] = React.useState(null);
  const [hoverPos, setHoverPos] = React.useState({
    x: 0,
    y: 0,
    compareY: null,
    tipLeft: 0,
    tipTop: 0,
    mouseY: 0,
  });
  const [tipStyle, setTipStyle] = React.useState(null);

  const vals = series.map((p) => Number(p?.[valueKey] || 0));
  const cVals = (compareSeries || []).map((p) => Number(p?.[compareValueKey] || 0));

  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const scaleX = (i, n) => {
    if (n <= 1) return padL;
    return padL + (i * innerW) / (n - 1);
  };

  const makeSmoothPath = (xs, ys) => {
    const n = xs.length;
    if (n === 0) return "";
    if (n === 1) return `M ${xs[0].toFixed(2)} ${ys[0].toFixed(2)}`;
    if (n === 2) {
      return `M ${xs[0].toFixed(2)} ${ys[0].toFixed(2)} L ${xs[1].toFixed(2)} ${ys[1].toFixed(2)}`;
    }
  
    let d = `M ${xs[0].toFixed(2)} ${ys[0].toFixed(2)}`;
  
    // ainda mais suave
    const t = 0.92;
  
    for (let i = 0; i < n - 1; i++) {
      const x0 = i > 0 ? xs[i - 1] : xs[i];
      const y0 = i > 0 ? ys[i - 1] : ys[i];

      const x1 = xs[i];
      const y1 = ys[i];

      const x2 = xs[i + 1];
      const y2 = ys[i + 1];

      const x3 = i + 2 < n ? xs[i + 2] : x2;
      const y3 = i + 2 < n ? ys[i + 2] : y2;
  
      let cp1x = x1 + ((x2 - x0) / 6) * t;
      let cp1y = y1 + ((y2 - y0) / 6) * t;
  
      let cp2x = x2 - ((x3 - x1) / 6) * t;
      let cp2y = y2 - ((y3 - y1) / 6) * t;
  
      // clamp mais apertado para remover "bicos"
      const localMin = Math.min(y1, y2);
      const localMax = Math.max(y1, y2);
      const overshoot = 8;
  
      cp1y = Math.max(localMin - overshoot, Math.min(localMax + overshoot, cp1y));
      cp2y = Math.max(localMin - overshoot, Math.min(localMax + overshoot, cp2y));
  
      d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)} ${cp2x.toFixed(2)} ${cp2y.toFixed(2)} ${x2.toFixed(
        2
      )} ${y2.toFixed(2)}`;
    }
  
    return d;
  };

  const formatDateShort = (s, fallbackLabel = "") => {
    if (fallbackLabel) return fallbackLabel;
    if (!s) return "";
    const d = new Date(`${s}T00:00:00`);
    if (Number.isNaN(d.getTime())) return String(s);
    return d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
  };

  const formatMoneyKAxis = (n) => {
    const v = Number(n || 0);
    if (!Number.isFinite(v) || v <= 0) return "€0";
    return `€${formatMoney(v, locale)}`;
  };

  const all = cVals.length ? vals.concat(cVals) : vals;
  const rawMax = Math.max(0, ...all, 0);

  const niceStep = (raw) => {
    if (!Number.isFinite(raw) || raw <= 0) return 1;
    const pow = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / pow;
    const choices = [1, 1.5, 2, 2.5, 5, 10];
    const chosen = choices.find((c) => norm <= c) || 10;
    return chosen * pow;
  };

  const TICKS = 5;
  const step = niceStep(rawMax / (TICKS - 1) || 1);
  const max = Math.max(step * (TICKS - 1), 1);
  const min = 0;
  const range = Math.max(1e-9, max - min);

  const scaleY = (v) => padT + innerH - ((v - min) * innerH) / range;

  const xs = vals.map((_, i) => scaleX(i, vals.length));
  const ys = vals.map((v) => scaleY(v));
  const dMain = makeSmoothPath(xs, ys);

  const cXs = cVals.map((_, i) => scaleX(i, cVals.length));
  const cYs = cVals.map((v) => scaleY(v));
  const dCompare = cVals.length ? makeSmoothPath(cXs, cYs) : null;

  const x0 = xs[0] ?? padL;
  const xLast = xs[xs.length - 1] ?? w - padR;
  const yBase = h - padB;

  const dArea = dMain
    ? `${dMain} L ${xLast.toFixed(2)} ${yBase.toFixed(2)} L ${x0.toFixed(2)} ${yBase.toFixed(2)} Z`
    : "";

  const yLevels = Array.from({ length: TICKS }, (_, i) => max - i * (max / (TICKS - 1)));

  const desiredXLabels =
    series.length <= 8 ? series.length : series.length <= 16 ? 6 : 8;

  const xIdxs = Array.from({ length: Math.max(2, desiredXLabels) }, (_, i) => {
    if (series.length <= 1) return 0;
    return Math.round((i * (series.length - 1)) / (Math.max(2, desiredXLabels) - 1));
  }).filter((v, i, arr) => arr.indexOf(v) === i);

  const onMove = (e) => {
    if (!wrapRef.current || !svgRef.current || series.length === 0) return;

    const wrapRect = wrapRef.current.getBoundingClientRect();
    const svgRect = svgRef.current.getBoundingClientRect();

    const xPx = e.clientX - svgRect.left;
    const xClamped = Math.max(0, Math.min(svgRect.width, xPx));
    const xSvg = (xClamped / svgRect.width) * w;

    const idx =
      series.length <= 1 ? 0 : Math.round(((xSvg - padL) / innerW) * (series.length - 1));

    const safeIdx = Math.max(0, Math.min(series.length - 1, idx));
    const x = scaleX(safeIdx, series.length);

    const y = scaleY(vals[safeIdx] ?? 0);

    const hasComparePoint =
      Array.isArray(compareSeries) &&
      compareSeries.length > safeIdx &&
      Number.isFinite(cVals[safeIdx]);

    const compareY = hasComparePoint ? scaleY(cVals[safeIdx] ?? 0) : null;

    const anchorY = compareY != null ? Math.min(y, compareY) : y;

    setHoverIdx(safeIdx);
    setHoverPos({
      x,
      y,
      compareY,

      // X continua preso ao ponto/data correta do gráfico
      tipLeft: svgRect.left - wrapRect.left + (x / w) * svgRect.width,

      // fallback baseado no ponto do gráfico
      tipTop: svgRect.top - wrapRect.top + (anchorY / h) * svgRect.height,

      // Y segue mesmo o rato dentro do wrapper
      mouseY: e.clientY - wrapRect.top,
    });
  };

  const onLeave = () => setHoverIdx(null);

  React.useLayoutEffect(() => {
    if (hoverIdx == null) {
      setTipStyle(null);
      return;
    }
    if (!wrapRef.current || !tipRef.current) return;

    const wrapRect = wrapRef.current.getBoundingClientRect();
    const tipRect = tipRef.current.getBoundingClientRect();

    const margin = 8;
    const offsetX = 18;
    const offsetY = 18;

    let left = (hoverPos.tipLeft || 0) + offsetX;

    // ✅ Y segue o rato, mas a caixa fica ligeiramente acima do cursor
    let top = (hoverPos.mouseY || hoverPos.tipTop || 0) - tipRect.height - offsetY;

    // Se bater na direita, passa para a esquerda do ponto
    if (left + tipRect.width + margin > wrapRect.width) {
      left = (hoverPos.tipLeft || 0) - tipRect.width - offsetX;
    }

    // Mantém sempre dentro do cartão
    left = Math.max(margin, Math.min(wrapRect.width - tipRect.width - margin, left));
    top = Math.max(margin, Math.min(wrapRect.height - tipRect.height - margin, top));

    setTipStyle({ left, top, transform: "translate(0,0)" });
  }, [hoverIdx, hoverPos.tipLeft, hoverPos.tipTop, hoverPos.mouseY]);

  const hovered = hoverIdx != null ? series[hoverIdx] : null;
  const hoveredValue = hoverIdx != null ? vals[hoverIdx] : null;

  const hoveredCompare =
    hoverIdx != null && Array.isArray(compareSeries) ? compareSeries[hoverIdx] : null;

  const hoveredCompareValue =
    hoverIdx != null && Array.isArray(compareSeries) ? cVals[hoverIdx] : null;

  const tooltipLabel = hovered
    ? formatDateShort(hovered.date, hovered.label || "")
    : "";

  const tooltipValue =
    hoveredValue != null ? `€${formatMoney(hoveredValue, locale)}` : "";

  const tooltipCompareValue =
    hoveredCompareValue != null ? `€${formatMoney(hoveredCompareValue, locale)}` : "";

  const deltaNum = Number(headlineDeltaPct || 0);
  const isUp = deltaNum > 0;
  const isDown = deltaNum < 0;

  return (
    <div
      ref={wrapRef}
      className={`dashboard-biglineWrap shopifyChart ${isShopifyLight ? "isLight" : ""}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <div className="dash-revHero">
        <div className="dash-revHeroLeft">
        <div className="dash-revHeroEyebrow">{t("panels.revenueTrendEyebrow")}</div>
          <div className="dash-revHeroValue">{formatMoneyHeadline(headlineValue)}</div>

          <div className="dash-revHeroDeltaRow">
            {isUp ? (
              <span className="dash-revHeroDelta isUp">
                ↗ +{formatPercentFull(Math.abs(deltaNum), locale)}%
              </span>
            ) : null}

            {isDown ? (
              <span className="dash-revHeroDelta isDown">
                ↘ {formatPercentFull(deltaNum, locale)}%
              </span>
            ) : null}

            {!isUp && !isDown ? (
              <span className="dash-revHeroDelta isFlat">• 0%</span>
            ) : null}
            <span className="dash-revHeroVs">{t("labels.vsPreviousPeriod")}</span>
          </div>
        </div>

        <div className="dash-revHeroRight">
          <div className="dash-legItem">
            <span className="dash-legDot solid" />
            {fromLabel} – {toLabel}
          </div>

          {compareSeries?.length > 0 && (compareFromLabel || compareToLabel) && (
            <div className="dash-legItem dash-legItemCompare">
              <span className="dash-legDot dotted" />
              {compareFromLabel} – {compareToLabel}
            </div>
          )}
        </div>
      </div>

      <svg
        ref={svgRef}
        className="dashboard-bigline dashboard-biglineShopify"
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="revLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(56,189,248,0.98)" />
            <stop offset="55%" stopColor="rgba(59,130,246,0.98)" />
            <stop offset="100%" stopColor="rgba(129,140,248,0.96)" />
          </linearGradient>

          <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(56,189,248,0.22)" />
            <stop offset="55%" stopColor="rgba(59,130,246,0.10)" />
            <stop offset="100%" stopColor="rgba(59,130,246,0)" />
          </linearGradient>
        </defs>

        <g className="dash-grid">
          {xIdxs.map((idx, i) => {
            const x = scaleX(idx, series.length);
            return (
              <line
                key={`vx-${i}`}
                x1={x}
                y1={padT}
                x2={x}
                y2={h - padB}
                className="dash-gridLine dash-gridLineV"
              />
            );
          })}

          {yLevels.map((tv, i) => {
            const y = scaleY(tv);
            return (
              <g key={`y-${i}`}>
                <line
                  x1={padL}
                  y1={y}
                  x2={w - padR}
                  y2={y}
                  className="dash-gridLine dash-gridLineH"
                />
                <text x={10} y={y + 4} className="dash-yLabel">
                  {formatMoneyKAxis(tv)}
                </text>
              </g>
            );
          })}
        </g>

        {dCompare && (
          <path
            d={dCompare}
            fill="none"
            className="dash-compareLine"
            stroke="rgba(148,163,184,.45)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {dArea ? <path d={dArea} fill="url(#revFill)" className="dash-area" /> : null}

        {dMain ? (
          <path
            d={dMain}
            fill="none"
            className="dashboard-biglinePath dash-mainLine"
            stroke="url(#revLine)"
            strokeWidth={isShopifyLight ? "3.2" : "2.6"}
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="1"
          />
        ) : null}

        <g className="dash-xAxis">
          {xIdxs.map((idx, i) => {
            const x = scaleX(idx, series.length);
            const d = series[idx]?.date;
            return (
            <text key={`x-${i}`} x={x} y={h - 12} textAnchor="middle" className="dash-xLabel">
              {formatDateShort(d, series[idx]?.label || "")}
            </text>
            );
          })}
        </g>

        <rect x="0" y="0" width={w} height={h} fill="transparent" />

        {hoverIdx != null && series.length > 0 && (
          <g className="dashboard-hoverLayer" opacity="0.98">
            <line
              x1={hoverPos.x}
              y1={padT}
              x2={hoverPos.x}
              y2={h - padB}
              className="dashboard-hoverVLine"
            />

            {hoverPos.compareY != null && (
              <>
                <circle
                  cx={hoverPos.x}
                  cy={hoverPos.compareY}
                  r="3"
                  className="dashboard-hoverDot dashboard-hoverDotCompare"
                />
                <circle
                  cx={hoverPos.x}
                  cy={hoverPos.compareY}
                  r="10"
                  className="dashboard-hoverHalo dashboard-hoverHaloCompare"
                />
              </>
            )}

            <circle
              cx={hoverPos.x}
              cy={hoverPos.y}
              r="3"
              className="dashboard-hoverDot dashboard-hoverDotCurrent"
            />
            <circle
              cx={hoverPos.x}
              cy={hoverPos.y}
              r="10"
              className="dashboard-hoverHalo dashboard-hoverHaloCurrent"
            />
          </g>
        )}
      </svg>

      {hoverIdx != null && (
        <div
          ref={tipRef}
          className="dashboard-chartTooltip dashboard-chartTooltipRevenue"
          style={
            tipStyle || {
              left: (hoverPos.tipLeft || 0) + 18,
              top: Math.max(8, (hoverPos.mouseY || hoverPos.tipTop || 0) - 120),
              transform: "translate(0,0)",
            }
          }
        >
          <div className="dashboard-chartTooltipDate">{tooltipLabel}</div>

          {hoveredCompare && tooltipCompareValue ? (
            <div className="dashboard-chartTooltipRow isCompare">
              <span className="dashboard-chartTooltipKey">
                {t("labels.previousPeriod")}:
              </span>
              <span className="dashboard-chartTooltipVal">{tooltipCompareValue}</span>
            </div>
          ) : null}

          <div className="dashboard-chartTooltipRow isCurrent">
            <span className="dashboard-chartTooltipKey">
              {t("kpis.revenueTotal")}:
            </span>
            <span className="dashboard-chartTooltipVal">{tooltipValue}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function StackedBars({ series = [], t, locale = "pt-PT" }) {
  const topRoundedRectPath = (x, y, w, h, r) => {
    const ww = Math.max(0, Number(w) || 0);
    const hh = Math.max(0, Number(h) || 0);
    if (ww <= 0 || hh <= 0) return "";

    const rr = Math.max(0, Math.min(Number(r) || 0, ww / 2, hh));
    const x0 = x, x1 = x + ww;
    const y0 = y, y1 = y + hh;

    if (rr <= 0) return `M ${x0} ${y0} H ${x1} V ${y1} H ${x0} Z`;

    return [
      `M ${x0} ${y1}`,
      `V ${y0 + rr}`,
      `Q ${x0} ${y0} ${x0 + rr} ${y0}`,
      `H ${x1 - rr}`,
      `Q ${x1} ${y0} ${x1} ${y0 + rr}`,
      `V ${y1}`,
      `Z`,
    ].join(" ");
  };

  const rows = Array.isArray(series) ? series : [];

  const w = 860;
  const h = 300;

  const padL = 52;
  const padR = 18;
  const padT = 14;
  const padB = 38;

  const wrapRef = React.useRef(null);
  const tipRef = React.useRef(null);

  const [hoverIdx, setHoverIdx] = React.useState(null);
  const [hoverPos, setHoverPos] = React.useState({ x: 0, y: 0, tipLeft: 0, tipTop: 0 });
  const [tipStyle, setTipStyle] = React.useState(null);

  const pVals = rows.map((d) => Number(d?.pickups || 0));
  const dVals = rows.map((d) => Number(d?.deliveries || 0));

  const rawMax = Math.max(0, ...pVals, ...dVals, 0);

  const niceStep = (raw) => {
    if (!Number.isFinite(raw) || raw <= 0) return 1;
    const pow = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / pow;
    const choices = [1, 2, 5, 10];
    const chosen = choices.find((c) => norm <= c) || 10;
    return chosen * pow;
  };

  const TICKS = 5;
  const step = niceStep(rawMax / (TICKS - 1) || 1);
  const max = Math.max(step * (TICKS - 1), 4);

  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const scaleX = (i, n) => {
    if (n <= 0) return padL;
    const slot = innerW / n;
    return padL + i * slot + slot / 2;
  };

  const scaleY = (v) => {
    const vv = Math.max(0, Number(v || 0));
    return padT + innerH - (vv / max) * innerH;
  };

  const formatDateShort = (s) => {
    if (!s) return "";
    const d = new Date(String(s));
    if (Number.isNaN(d.getTime())) return String(s);
    return d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
  };

  // ✅ X labels: se for 14 dias ou menos, mostra TODOS os dias (como a imagem 2)
  const xIdxs =
    rows.length <= 10
      ? rows.map((_, i) => i)
      : Array.from({ length: 7 }, (_, i) => {
          if (rows.length <= 1) return 0;
          return Math.round((i * (rows.length - 1)) / 6);
        }).filter((v, i, arr) => arr.indexOf(v) === i);

  // ✅ yLevels inclui 0 (a imagem 2 mostra 0)
  const yLevels = Array.from({ length: TICKS }, (_, i) => {
    const frac = (TICKS - 1 - i) / (TICKS - 1); // 1..0
    return max * frac;
  });

  const onMove = (e) => {
    if (!wrapRef.current || rows.length === 0) return;

    const rect = wrapRef.current.getBoundingClientRect();
    const xPx = e.clientX - rect.left;
    const xClamped = Math.max(0, Math.min(rect.width, xPx));
    const xSvg = (xClamped / rect.width) * w;

    const slot = innerW / rows.length;
    const idx = Math.floor((xSvg - padL) / slot);
    const safeIdx = Math.max(0, Math.min(rows.length - 1, idx));

    const cx = scaleX(safeIdx, rows.length);
    const topY = Math.min(scaleY(pVals[safeIdx] || 0), scaleY(dVals[safeIdx] || 0));

    setHoverIdx(safeIdx);
    setHoverPos({
      x: cx,
      y: topY,
      tipLeft: (cx / w) * rect.width,
      tipTop: (topY / h) * rect.height,
    });
  };

  const onLeave = () => setHoverIdx(null);

  React.useLayoutEffect(() => {
    if (hoverIdx == null) {
      setTipStyle(null);
      return;
    }
    if (!wrapRef.current || !tipRef.current) return;

    const wrapRect = wrapRef.current.getBoundingClientRect();
    const tipRect = tipRef.current.getBoundingClientRect();

    const margin = 8;
    const offsetX = 18;
    const offsetY = 16;

    const pointLeft = hoverPos.tipLeft || 0;
    const pointTop = hoverPos.tipTop || 0;

    // ✅ Tooltip perto da barra, mas acima dela
    let left = pointLeft + offsetX;
    let top = pointTop - tipRect.height - offsetY;

    // ✅ Se bater à direita, passa para a esquerda da barra
    if (left + tipRect.width + margin > wrapRect.width) {
      left = pointLeft - tipRect.width - offsetX;
    }

    // ✅ Se estiver demasiado em cima, fica abaixo do ponto/barra
    // mas sem ir para o header do cartão
    if (top < margin) {
      top = pointTop + offsetY;
    }

    // ✅ Mantém dentro do gráfico, sem ficar solto no topo
    left = Math.max(margin, Math.min(wrapRect.width - tipRect.width - margin, left));
    top = Math.max(margin, Math.min(wrapRect.height - tipRect.height - margin, top));

    setTipStyle({ left, top, transform: "translate(0,0)" });
  }, [hoverIdx, hoverPos.tipLeft, hoverPos.tipTop]);

  const hovered = hoverIdx != null ? rows[hoverIdx] : null;
  const hoveredPickups = hoverIdx != null ? pVals[hoverIdx] ?? 0 : 0;
  const hoveredDeliveries = hoverIdx != null ? dVals[hoverIdx] ?? 0 : 0;

  const slot = rows.length ? innerW / rows.length : 0;

  // barras bem mais largas e mais “premium”
  const groupW = Math.max(28, Math.min(46, slot * 0.50));
  const gap = Math.max(4, Math.min(8, groupW * 0.16));
  const barW = (groupW - gap) / 2;

  const r = 5;

  return (
    <div ref={wrapRef} className="dashboard-pdBarsWrap" onMouseMove={onMove} onMouseLeave={onLeave}>
      <svg className="dashboard-stackedbars" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
        <defs>
          /* Deliveries (cyan - slightly darker cyan) */
          <linearGradient id="pdDeliveriesGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(34,211,238,.98)" />
            <stop offset="100%" stopColor="rgba(6,182,212,.88)" />
          </linearGradient>

          /* Pickups (emerald - slightly darker emerald) */
          <linearGradient id="pdPickupsGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(16,185,129,.98)" />
            <stop offset="100%" stopColor="rgba(5,150,105,.88)" />
          </linearGradient>
        </defs>

        <g className="pdbar-grid">
          {yLevels.map((tv, i) => {
            const y = scaleY(tv);
            return (
              <g key={`y-${i}`}>
                <line x1={padL} y1={y} x2={w - padR} y2={y} className="pdbar-gridLine" />
                <text x={6} y={y + 4} className="pdbar-yLabel">
                  {formatCount(tv, locale)}
                </text>
              </g>
            );
          })}
        </g>

        {rows.map((d, i) => {
          const cx = scaleX(i, rows.length);
          const p = pVals[i] || 0;
          const dl = dVals[i] || 0;

          const x0 = cx - groupW / 2;

          const pY = scaleY(p);
          const dY = scaleY(dl);

          const yBase = padT + innerH;

          const pH = Math.max(0, yBase - pY);
          const dH = Math.max(0, yBase - dY);

          const isHover = hoverIdx === i;

          return (
            <g key={d?.date || i} className={`pdbar-group ${isHover ? "isHover" : ""}`}>
              {(() => {
                // faixa vertical = 1 slot inteiro do dia
                const slotW = innerW / Math.max(1, rows.length);
                const bandX = padL + i * slotW;
                return (
                  isHover && (
                    <rect
                      x={bandX + 4}
                      y={padT}
                      width={Math.max(0, slotW - 8)}
                      height={innerH}
                      rx="12"
                      className="pdbar-hoverBand"
                    />
                  )
                );
              })()}

              {(() => {
                // ✅ largura/spacing mais “blocky” como imagem 1
                const rTop = 6;
                const minH = 2;

                const dPath = topRoundedRectPath(x0, dY, barW, Math.max(minH, dH), rTop);
                const pPath = topRoundedRectPath(x0 + barW + gap, pY, barW, Math.max(minH, pH), rTop);

                return (
                  <>
                    <path
                      d={dPath}
                      className="dashboard-bar-deliveries pdbar-bar"
                      fill="url(#pdDeliveriesGrad)"
                    />
                    <path
                      d={pPath}
                      className="dashboard-bar-pickups pdbar-bar"
                      fill="url(#pdPickupsGrad)"
                    />
                  </>
                );
              })()}
            </g>
          );
        })}

        <g className="pdbar-xAxis">
          {xIdxs.map((idx, i) => {
            const x = scaleX(idx, rows.length);
            const d = rows[idx]?.date;
            return (
              <text key={`x-${i}`} x={x} y={h - 8} textAnchor="middle" className="pdbar-xLabel">
                {formatDateShort(d)}
              </text>
            );
          })}
        </g>

        {hoverIdx != null && rows.length > 0 && (
          <g className="pdbar-hoverLayer" opacity="0.98">
            <line x1={hoverPos.x} y1={padT} x2={hoverPos.x} y2={padT + innerH} className="pdbar-hoverVLine" />
          </g>
        )}
      </svg>

      {hoverIdx != null && hovered && (
        <div
          ref={tipRef}
          className="dashboard-chartTooltip pdTooltip"
          style={
            tipStyle || {
              left: (hoverPos.tipLeft || 0) + 18,
              top: Math.max(8, (hoverPos.mouseY || hoverPos.tipTop || 0) - 120),
              transform: "translate(0,0)",
            }
          }
        >
          <div className="dashboard-chartTooltipDate">{formatDateShort(hovered.date)}</div>

          <div className="pdTipLine deliveries">
            <span className="pdTipKey">{t("labels.deliveries")}:</span>
            <span className="pdTipNum">{formatCount(hoveredDeliveries, locale)}</span>
          </div>

          <div className="pdTipLine pickups">
            <span className="pdTipKey">{t("labels.pickups")}:</span>
            <span className="pdTipNum">{formatCount(hoveredPickups, locale)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function DonutChart({ items = [], t, locale = "pt-PT" }) {
  const safeItems = Array.isArray(items) ? items : [];

  const formatStatusLabel = (label) => {
    const raw = String(label || "").toUpperCase();

    const map = {
      COMPLETED: t("status.completed"),
      IN_TRANSIT: t("status.inTransit"),
      PENDING: t("status.pending"),
      LATE: t("status.late"),
      CANCELLED: t("status.cancelled"),
      LOADING_MERCHANDISE: t("status.loadingMerchandise"),
      UNLOADING_MERCHANDISE: t("status.unloadingMerchandise"),
    };

    return map[raw] || label;
  };

  const visibleItems = safeItems
    .map((it) => ({
      ...it,
      value: Number(it?.value || 0),
      prettyLabel: formatStatusLabel(it?.label),
    }))
    .filter((it) => it.value > 0)
    .slice(0, 5);

  const w = 260;
  const h = 260;
  const cx = 130;
  const cy = 130;
  const r = 78;
  const stroke = 24;

  const totalRaw = visibleItems.reduce((acc, it) => acc + Number(it.value || 0), 0);
  const total = totalRaw || 1;

  const classes = ["d0", "d1", "d2", "d3", "d4"];
  const len = 2 * Math.PI * r;
  const GAP_PX = 4;

  let acc = 0;

  return (
    <div className="dashboard-donutWrap dashboard-donutWrapRef">
      <div className="dashboard-donutStage dashboard-donutStageRef">
        <svg className="dashboard-donut dashboard-donutRef" viewBox={`0 0 ${w} ${h}`}>
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.04)"
            strokeWidth={stroke}
          />

          {visibleItems.map((it, idx) => {
            const v = Number(it.value || 0);
            const frac = v / total;
            const dash = Math.max(3, frac * len - GAP_PX);
            const gap = len - dash;
            const rotation = (acc / total) * 360 - 90;
            acc += v;

            const sliceClass = classes[idx % classes.length];

            return (
              <circle
                key={`${it.label}-${idx}`}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${gap}`}
                strokeLinecap="butt"
                transform={`rotate(${rotation} ${cx} ${cy})`}
                className={`dashboard-donutSlice ${sliceClass}`}
              />
            );
          })}

          <circle
            cx={cx}
            cy={cy}
            r={r - stroke + 12}
            fill="rgba(8,10,22,0.88)"
          />

          <text x={cx} y={cy - 4} textAnchor="middle" className="dashboard-donutTotal">
            {formatCount(totalRaw, locale)}
          </text>
          <text x={cx} y={cy + 20} textAnchor="middle" className="dashboard-donutSub">
            {t("labels.total")}
          </text>
        </svg>
      </div>

      <div className="dashboard-donutLegend dashboard-donutLegendRef">
        {visibleItems.map((it, idx) => (
          <div className="dashboard-legendRow dashboard-legendRowRef" key={`${it.label}-${idx}`}>
            <span className={`dashboard-legendDot ${classes[idx % classes.length]}`} />
            <span className="dashboard-legendLabel">{it.prettyLabel}</span>
            <span className="dashboard-legendValue">{formatCount(it.value, locale)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenuePerTruckBars({ rows = [], loading, t, locale = "pt-PT" }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const max = Math.max(1, ...safeRows.map((r) => Number(r?.revenue || 0)));

  const moneyNoDecimals = (v) => {
    return formatMoney(v, locale);
  };

  if (!safeRows.length) {
    return (
      <p className="dash-revTruckEmpty">
        {loading ? t("labels.loading") : t("labels.noData")}
      </p>
    );
  }

  return (
    <div className="dash-revTruckList">
      {safeRows.map((r, i) => {
        const rev = Number(r?.revenue || 0);
        const pct = Math.max(2, Math.min(100, (rev / max) * 100));

        return (
          <div
            className="dash-revTruckRow"
            key={`${r?.label ?? (typeof t === "function" ? t("labels.truck") : "truck")}-${i}`}
          >
            <div className="dash-revTruckRowTop">
              <span className="dash-revTruckLabel">
                {String(r?.label || "").slice(0, 18)}
              </span>
              <span className="dash-revTruckValue">
                €{moneyNoDecimals(rev)}
              </span>
            </div>

            <div className="dash-revTruckTrack" aria-hidden="true">
              <div
                className={`dash-revTruckFill ${i % 2 === 0 ? "isCyan" : "isPurple"}`}
                style={{
                  width: `${pct}%`,
                  animationDelay: `${0.5 + i * 0.06}s`,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DashboardClock({ locale, t }) {
  const [currentTime, setCurrentTime] = React.useState(new Date());

  React.useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const greeting = (() => {
    const h = currentTime.getHours();
    if (h < 12) return t("greetings.morning");
    if (h < 18) return t("greetings.afternoon");
    return t("greetings.evening");
  })();

  return (
    <div className="dashboard-topbarClock">
      <div className="dashboard-topbarGreeting">{greeting}</div>
      <div className="dashboard-topbarTime">
        {currentTime.toLocaleTimeString(locale)}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t, i18n } = useTranslation("dashboard");

  const currentLocale = useMemo(() => {
    // Dashboard numérico:
    // 1 234 567,89 em vez de 1.234.567,89
    return "fr-FR";
  }, []);

  const [periodId, setPeriodId] = useState("monthly");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const { from, to } = useMemo(() => {
    if (periodId === "custom") {
      const fallback = computeRange("monthly");
      return { from: customFrom || fallback.from, to: customTo || fallback.to };
    }
    return computeRange(periodId);
  }, [periodId, customFrom, customTo]);

  const currentRangeLabel = useMemo(
    () => formatRangeLabel(from, to, currentLocale),
    [from, to, currentLocale]
  );

  const dashboardCompanyScope = useMemo(() => getDashboardCompanyScope(), []);

  const dashboardCacheKey = useMemo(
    () =>
      buildDashboardCacheKey({
        from,
        to,
        locale: currentLocale,
        companyScope: dashboardCompanyScope,
      }),
    [from, to, currentLocale, dashboardCompanyScope]
  );

  const previousRange = useMemo(
    () => computePreviousRange(periodId, from, to),
    [periodId, from, to]
  );

  const previousRangeLabel = useMemo(
    () => formatRangeLabel(previousRange.from, previousRange.to, currentLocale),
    [previousRange.from, previousRange.to, currentLocale]
  );

  // ✅ Invoice rollups (only used when the selected range is "today")
  const [revenueTiles, setRevenueTiles] = useState(null);
  const [tilesErr, setTilesErr] = useState("");
  const isTodayRange = useMemo(() => {
    const today = todayYMDLisbon();
    return from === today && to === today;
  }, [from, to]);

  const [topClientsTab, setTopClientsTab] = useState("industries"); // "industries" | "carriers"

  function formatMoneyTopClients(v) {
    return `€${formatMoney(v, currentLocale)}`;
  }

  // ✅ custom dropdown period
  const [isPeriodOpen, setIsPeriodOpen] = useState(false);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  function openDatePicker(ref) {
    const el = ref?.current;
    if (!el) return;

    // Chrome/Edge (e outros Chromium) suportam isto ✅
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch (e) {
        // fallback
      }
    }

    // fallback (Safari / browsers sem showPicker)
    el.focus();
  }

  const periodRef = useRef(null);

  const periodOptions = useMemo(
    () => [
      { id: "daily", label: t("period.daily") },
      { id: "weekly", label: t("period.weekly") },
      { id: "monthly", label: t("period.monthly") },
      { id: "yearly", label: t("period.yearly") },
      { id: "custom", label: t("period.custom") },
    ],
    [t]
  );

  const currentPeriodLabel = periodOptions.find((o) => o.id === periodId)?.label ?? "";

  const quickOpsSubLabel =
    periodId === "daily"
      ? t("kpis.quickOpsToday")
      : periodId === "weekly"
      ? t("kpis.quickOpsWeek")
      : periodId === "monthly"
      ? t("kpis.quickOpsMonth")
      : periodId === "yearly"
      ? t("kpis.quickOpsYear")
      : t("kpis.quickOpsRange");

  const opsActivitySubLabel =
    periodId === "daily"
      ? t("kpis.opsActivityToday")
      : periodId === "weekly"
      ? t("kpis.opsActivityWeek")
      : periodId === "monthly"
      ? t("kpis.opsActivityMonth")
      : periodId === "yearly"
      ? t("kpis.opsActivityYear")
      : t("kpis.opsActivityRange");

  useEffect(() => {
    function onDown(e) {
      if (!isPeriodOpen) return;
      if (e.key === "Escape") setIsPeriodOpen(false);
    }

    function onClickOutside(e) {
      if (!isPeriodOpen) return;
      if (periodRef.current && !periodRef.current.contains(e.target)) {
        setIsPeriodOpen(false);
      }
    }

    window.addEventListener("keydown", onDown);
    window.addEventListener("mousedown", onClickOutside);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("mousedown", onClickOutside);
    };
  }, [isPeriodOpen]);

  async function fetchRevenueTilesToday({ currency = "EUR" } = {}) {
    setTilesErr("");

    // Only fetch when range is exactly today in Lisbon
    if (!isTodayRange) {
      setRevenueTiles(null);
      return;
    }

    try {
      const resp = await axios.get(
        `/invoices/rollups/revenue-tiles?currency=${encodeURIComponent(currency)}`
      );
      console.log("[Dashboard] revenue-tiles OK", {
        status: resp.status,
        data: resp.data,
      });
      setRevenueTiles(resp.data || null);
    } catch (e) {
      console.error("[Dashboard] fetch revenue-tiles error:", e);
      console.error("[Dashboard] revenue-tiles status:", e?.response?.status);
      console.error("[Dashboard] revenue-tiles data:", e?.response?.data);
      console.error("[Dashboard] revenue-tiles url:", e?.config?.url);
      setRevenueTiles(null);
      setTilesErr(t("errors.fetchRevenueTilesFailed"));
    }
  }

  useEffect(() => {
    fetchRevenueTilesToday({ currency: "EUR" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, isTodayRange]);

  const cachedDashboardOnMount = useMemo(
    () => readDashboardCache(dashboardCacheKey),
    [dashboardCacheKey]
  );

  const [loading, setLoading] = useState(() => !cachedDashboardOnMount?.data);
  const [dashboardData, setDashboardData] = useState(() => cachedDashboardOnMount?.data || null);
  const [dashboardErr, setDashboardErr] = useState("");
  const [dashboardEtag, setDashboardEtag] = useState(() => cachedDashboardOnMount?.etag || "");

  useEffect(() => {
    const cached = readDashboardCache(dashboardCacheKey);

    setDashboardData(cached?.data || null);
    setDashboardEtag(cached?.etag || "");
    setLoading(!cached?.data);
  }, [dashboardCacheKey]);

  useEffect(() => {
    let cancelled = false;

    async function fetchDashboardSummary() {
      try {
        setDashboardErr("");

        const cached = readDashboardCache(dashboardCacheKey);
        const currentData = cached?.data || dashboardData;
        const requestEtag = cached?.etag || dashboardEtag || "";

        // Só mostra loading se não houver absolutamente nada para renderizar
        if (!currentData) {
          setLoading(true);
        } else {
          setLoading(false);
        }

        const resp = await axios.get("/dashboard/summary", {
          params: { from, to },
          headers: requestEtag ? { "If-None-Match": requestEtag } : {},
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 304,
        });

        if (cancelled) return;

        if (resp.status === 304) {
          console.log("[Dashboard] summary 304 - reusing cached data");

          if (cached?.data) {
            setDashboardData(cached.data);
          }
          if (cached?.etag) {
            setDashboardEtag(cached.etag);
          }

          return;
        }

        console.log("[Dashboard] summary OK", {
          status: resp.status,
          data: resp.data,
          cache: resp.headers?.["x-dashboard-cache"],
        });

        console.log("[Dashboard] raw summary payload:", resp.data);

        const nextData = resp.data || null;
        const nextEtag = resp.headers?.etag || "";

        setDashboardData(nextData);
        setDashboardEtag(nextEtag);

        writeDashboardCache(dashboardCacheKey, {
          etag: nextEtag,
          data: nextData,
        });
      } catch (e) {
        if (cancelled) return;

        console.error("[Dashboard] summary error:", e);
        console.error("[Dashboard] summary status:", e?.response?.status);
        console.error("[Dashboard] summary data:", e?.response?.data);
        console.error("[Dashboard] summary url:", e?.config?.url);

        const cached = readDashboardCache(dashboardCacheKey);

        if (cached?.data) {
          setDashboardData(cached.data);
          setDashboardEtag(cached.etag || "");
        } else {
          setDashboardData(null);
        }

        setDashboardErr(t("errors.fetchDashboardSummaryFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDashboardSummary();

    return () => {
      cancelled = true;
    };
  }, [from, to, dashboardCacheKey, t]);

  const periodKpis = dashboardData?.periodKpis || {};
  const liveKpis = dashboardData?.liveKpis || {};
  const dataQuality = dashboardData?.dataQuality || {};
  const dashboardMeta = dashboardData?.meta || {};
  const dashboardRevision = dashboardMeta?.revision || "";

  const revenueComparison = dashboardData?.comparison || {};

  const revenueDeltaPct = Number(revenueComparison?.revenueDeltaPct || 0);
  const revenueDeltaAbs = Number(revenueComparison?.revenueDeltaAbs || 0);
  const revenuePreviousTotal = Number(revenueComparison?.revenuePreviousTotal || 0);

  const revenueTrendDirection =
    revenueDeltaPct > 0 ? "up" : revenueDeltaPct < 0 ? "down" : "flat";

  const topClientsLoadsMissingCount = Number(dataQuality?.loadsMissingClientLink || 0);
  const revenuePerTruckExcludedCount =
    Number(dataQuality?.loadsMissingTruckLinkForRevenue || 0) +
    Number(dataQuality?.loadsWithSplitTruckForRevenue || 0);

  const k = {
    revenueTotal: Number(periodKpis?.revenueTotal || 0),
    invoicesPaidTotal: Number(periodKpis?.invoicesPaidTotal || 0),
    invoicesOverdueTotal: Number(periodKpis?.invoicesOverdueTotal || 0),
    loadsActiveNow: Number(liveKpis?.loadsActiveNow || 0),
    loadsTouchedInRange: Number(periodKpis?.loadsTouchedInRange || 0),
    pickupsInRange: Number(liveKpis?.pickupsInRange || 0),
    deliveriesInRange: Number(liveKpis?.deliveriesInRange || 0),
    latePointsInRange: Number(liveKpis?.latePointsInRange || 0),
    dsoDaysAvg:
      periodKpis?.dsoDaysAvg != null
        ? Number(periodKpis.dsoDaysAvg)
        : null,
    invoicesOpenTotal: Number(periodKpis?.invoicesOpenTotal || 0),
    pendingPointsNow: Number(liveKpis?.pendingPointsNow || 0),
    pendingPointsInRange: Number(periodKpis?.pendingPointsInRange || 0),
    unassignedNow: {
      any: liveKpis?.unassignedNow?.any ?? 0,
      truck: liveKpis?.unassignedNow?.truck ?? 0,
      driver: liveKpis?.unassignedNow?.driver ?? 0,
      trailer: liveKpis?.unassignedNow?.trailer ?? 0,
    },
  };

  const anyUnassigned = k.unassignedNow?.any ?? 0;
  const unassigned = k.unassignedNow ?? { any: 0, truck: 0, driver: 0, trailer: 0 };

  const revenueBucket = useMemo(
    () => getRevenueBucket(periodId, from, to),
    [periodId, from, to]
  );

  const revenueSeriesRaw = Array.isArray(dashboardData?.timeseries?.revenuePerDay)
    ? dashboardData.timeseries.revenuePerDay.map((r) => ({
        date: r.date,
        value: Number(r?.revenue || 0),
      }))
    : [];

  const revenueCompareSeriesRaw = Array.isArray(dashboardData?.timeseries?.revenuePerDayPrevious)
    ? dashboardData.timeseries.revenuePerDayPrevious.map((r) => ({
        date: r.date,
        value: Number(r?.revenue || 0),
      }))
    : [];

  const revenueSeries = useMemo(
    () => aggregateRevenueSeries(revenueSeriesRaw, revenueBucket, currentLocale),
    [revenueSeriesRaw, revenueBucket, currentLocale]
  );

  const revenueCompareSeries = useMemo(
    () => aggregateRevenueSeries(revenueCompareSeriesRaw, revenueBucket, currentLocale),
    [revenueCompareSeriesRaw, revenueBucket, currentLocale]
  );

  const stacked14 = Array.isArray(dashboardData?.timeseries?.pdPerDay)
    ? dashboardData.timeseries.pdPerDay
        .map((r) => ({
          date: r.date,
          pickups: Number(r?.pickups || 0),
          deliveries: Number(r?.deliveries || 0),
        }))
        // remove dias completamente vazios para evitar aquele “deserto” no gráfico
        .filter((r) => r.pickups > 0 || r.deliveries > 0)
        // mantém apenas os últimos 14 dias com atividade
        .slice(-14)
    : [];

  const revenuePerTruck = Array.isArray(dashboardData?.revenuePerTruck)
    ? dashboardData.revenuePerTruck
    : [];

  const complianceDueSoon = Array.isArray(dashboardData?.complianceDueSoon)
    ? dashboardData.complianceDueSoon
    : [];

  const topClientsLoads = Array.isArray(dashboardData?.topClientsLoads)
    ? dashboardData.topClientsLoads
    : [];

  const topClientsIndustries = Array.isArray(dashboardData?.topClientsIndustries)
    ? dashboardData.topClientsIndustries
    : [];

  const topClientsCarriers = Array.isArray(dashboardData?.topClientsCarriers)
    ? dashboardData.topClientsCarriers
    : [];

  const topClientsActive =
    topClientsTab === "carriers" ? topClientsCarriers : topClientsIndustries;

  const topClientsMax = Math.max(
    1,
    ...topClientsActive.map((c) => Number(c?.revenue || 0))
  );

  const statusItems = Object.entries(
    dashboardData?.breakdown?.pickupDeliveriesStatus || {}
  ).map(([label, value]) => ({
    label,
    value: Number(value || 0),
  }));

  const counts = {
    trucks: dashboardData?.counts?.trucks ?? 0,
    trailers: dashboardData?.counts?.trailers ?? 0,
    drivers: dashboardData?.counts?.drivers ?? 0,
    clients: dashboardData?.counts?.clients ?? 0,
  };

  const affectedMetrics = {
    topClientsLoads: topClientsLoadsMissingCount > 0,
    revenuePerTruck: revenuePerTruckExcludedCount > 0,
  };

  // console.log("[Dashboard] active revision:", dashboardRevision);

  return (
    <div className="dashboard-page">
      {/* ✅ Background decorative elements (igual ao design referência) */}
      <div className="dashboard-bgDecor" aria-hidden="true">
        <div className="dashboard-blob dashboard-blob-cyan" />
        <div className="dashboard-blob dashboard-blob-violet" />
        <div className="dashboard-blob dashboard-blob-pink" />
      </div>

      <div className="dashboard-shell">
        <div className="dashboard-topbar">
          <div className="dashboard-topbarMain">
            <div className="dashboard-topbarBrand">
              <div className="dashboard-topbarIconWrap">
                <LayoutDashboard className="dashboard-topbarIcon" strokeWidth={2.15} />
              </div>

              <div className="dashboard-titleWrap">
                <div className="dashboard-title">{t("title")}</div>
                <div className="dashboard-subtitleFixed">{t("header.controlPanel")}</div>
              </div>
            </div>

            <div className="dashboard-topbarCenter">
              <div className="dashboard-periodWrap" ref={periodRef}>
                <button
                  type="button"
                  className={`dashboard-periodBtn ${isPeriodOpen ? "isOpen" : ""}`}
                  onClick={() => setIsPeriodOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={isPeriodOpen}
                >
                  <FaRegCalendarAlt className="dashboard-calIcon" />
                  <span className="dashboard-periodText">{currentPeriodLabel}</span>
                  <FaChevronDown className="dashboard-periodChevron" />
                </button>

                {isPeriodOpen && (
                  <div className="dashboard-periodMenu" role="menu">
                    {periodOptions.map((opt) => {
                      const active = opt.id === periodId;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          className={`dashboard-periodItem ${active ? "isActive" : ""}`}
                          role="menuitem"
                          onClick={() => {
                            setPeriodId(opt.id);
                            setIsPeriodOpen(false);
                          }}
                        >
                          <span className="dashboard-periodItemLabel">{opt.label}</span>
                          {active ? <FaCheck className="dashboard-periodCheck" /> : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="dashboard-topbarRight">
              <DashboardClock locale={currentLocale} t={t} />
            </div>
          </div>

          {periodId === "custom" && (
            <div className="dashboard-customRange dashboard-customRange--header">
              <div className="dashboard-dateField">
                <label className="dashboard-dateLabel">{t("filters.from")}</label>

                <div className="dashboard-dateInputWrap">
                  <input
                    ref={fromDateRef}
                    className="dashboard-dateInput"
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    onClick={() => openDatePicker(fromDateRef)}
                    onFocus={() => openDatePicker(fromDateRef)}
                  />
                  <button
                    type="button"
                    className="dashboard-dateIconBtn"
                    onClick={() => openDatePicker(fromDateRef)}
                    aria-label={t("filters.from")}
                  >
                    <FaRegCalendarAlt />
                  </button>
                </div>
              </div>

              <div className="dashboard-dateField">
                <label className="dashboard-dateLabel">{t("filters.to")}</label>

                <div className="dashboard-dateInputWrap">
                  <input
                    ref={toDateRef}
                    className="dashboard-dateInput"
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    onClick={() => openDatePicker(toDateRef)}
                    onFocus={() => openDatePicker(toDateRef)}
                  />
                  <button
                    type="button"
                    className="dashboard-dateIconBtn"
                    onClick={() => openDatePicker(toDateRef)}
                    aria-label={t("filters.to")}
                  >
                    <FaRegCalendarAlt />
                  </button>
                </div>
              </div>
            </div>
          )}

          {dashboardErr ? (
            <div className="dashboard-errorBanner">
              {dashboardErr}
            </div>
          ) : null}
        </div>

        {/* KPI Cards */}
        <div className="dashboard-kpiRows dashboard-kpiRows--modern">
          <div className="dashboard-kpiRowTop dashboard-kpiRowTop--modern">
            <KpiCard
              title={t("kpis.revenueTotal")}
              value={`€${formatMoney(
                isTodayRange && revenueTiles?.tiles?.daily?.grossSum != null
                  ? Number(revenueTiles.tiles.daily.grossSum)
                  : k.revenueTotal,
                currentLocale
              )}`}
              sub={
                `${isTodayRange
                  ? t("kpis.invoicesHintTodayRollup")
                  : t("kpis.invoicesHint")}${tilesErr ? ` • ${tilesErr}` : ""}`
              }
              icon={FaEuroSign}
              color="blue"
            />

            <KpiCard
              title={t("kpis.invoicesPaid")}
              value={`€${formatMoney(k.invoicesPaidTotal, currentLocale)}`}
              sub={
                k.dsoDaysAvg != null
                  ? t("kpis.dsoHint", { dso: formatFullNumber(k.dsoDaysAvg, currentLocale) })
                  : t("kpis.dsoHintNA")
              }
              icon={FaFileInvoice}
              color="green"
            />

            <KpiCard
              title={t("kpis.invoicesOverdue")}
              value={`€${formatMoney(k.invoicesOverdueTotal, currentLocale)}`}
              sub={t("kpis.overdueHint")}
              chip={t("labels.action")}
              chipAction
              icon={FaClock}
              color="amber"
            />

            <KpiCard
              title={t("kpis.loadsActiveNow")}
              value={formatCount(k.loadsActiveNow ?? 0, currentLocale)}
              sub={t("kpis.loadsTouchedInRangeHint", {
                count: formatCount(k.loadsTouchedInRange ?? 0, currentLocale),
              })}
              icon={FaCube}
              color="purple"
            />
          </div>

          <div className="dashboard-kpiRowBottom dashboard-kpiRowBottom--modern">
            <KpiCard
              title={
                periodId === "daily"
                  ? t("kpis.pickupsToday")
                  : periodId === "weekly"
                  ? t("kpis.pickupsWeek")
                  : periodId === "monthly"
                  ? t("kpis.pickupsMonth")
                  : periodId === "yearly"
                  ? t("kpis.pickupsYear")
                  : t("kpis.pickupsRange")
              }
              value={formatCount(k.pickupsInRange ?? 0, currentLocale)}
              sub={opsActivitySubLabel}
              icon={FaArrowDown}
              color="teal"
            />

            <KpiCard
              title={
                periodId === "daily"
                  ? t("kpis.deliveriesToday")
                  : periodId === "weekly"
                  ? t("kpis.deliveriesWeek")
                  : periodId === "monthly"
                  ? t("kpis.deliveriesMonth")
                  : periodId === "yearly"
                  ? t("kpis.deliveriesYear")
                  : t("kpis.deliveriesRange")
              }
              value={formatCount(k.deliveriesInRange ?? 0, currentLocale)}
              sub={opsActivitySubLabel}
              icon={FaArrowUp}
              color="navy"
            />

            <KpiCard
              title={t("kpis.latePoints")}
              value={formatCount(k.latePointsInRange ?? 0, currentLocale)}
              sub={t("kpis.lateHintInRange")}
              chip={k.latePointsInRange > 0 ? t("labels.action") : undefined}
              chipAction={Boolean(k.latePointsInRange > 0)}
              icon={FaExclamationTriangle}
              color="pink"
            />
          </div>
        </div>

        <div className="dashboard-row2">
          {/* LEFT: Tendência de faturação (REAL DATA) */}
          <div className="dashboard-panel dashboard-glass dash-revTrendCard">
            <BigLineChart
              series={revenueSeries}
              valueKey="value"
              compareSeries={revenueCompareSeries}
              compareValueKey="value"
              fromLabel={currentRangeLabel.fromLabel}
              toLabel={currentRangeLabel.toLabel}
              compareFromLabel={previousRangeLabel.fromLabel}
              compareToLabel={previousRangeLabel.toLabel}
              variant="shopifyLight"
              headlineValue={revenueComparison?.revenueCurrentTotal ?? k.revenueTotal}
              headlineDeltaPct={revenueComparison?.revenueDeltaPct ?? 0}
              t={t}
              locale={currentLocale}
            />
          </div>

          {/* RIGHT: Top clientes (faturação) — mantém o que acabaste de adicionar */}
          <div className="dashboard-panel dashboard-glass dash-topClientsCard">
            {/* Header */}
            <div className="dash-topClientsHead">
              <div className="dash-topClientsTitleRow">
                <Users className="dash-topClientsIcon" strokeWidth={2.1} />
                <div className="dash-topClientsTitle">{t("panels.topClients")}</div>
              </div>

              <div
                className="dash-topClientsTabs"
                role="tablist"
                aria-label={t("aria.topClientsTabs")}
              >
                <button
                  type="button"
                  className={`dash-topClientsTab ${topClientsTab === "industries" ? "isActive" : ""}`}
                  onClick={() => setTopClientsTab("industries")}
                  role="tab"
                  aria-selected={topClientsTab === "industries"}
                >
                  <Factory className="dash-topClientsTabIco" strokeWidth={2.1} />
                  <span>{t("labels.industry")}</span>
                </button>

                <button
                  type="button"
                  className={`dash-topClientsTab ${topClientsTab === "carriers" ? "isActive" : ""}`}
                  onClick={() => setTopClientsTab("carriers")}
                  role="tab"
                  aria-selected={topClientsTab === "carriers"}
                >
                  <Truck className="dash-topClientsTabIco" strokeWidth={2.1} />
                  <span>{t("labels.carrier")}</span>
                </button>
              </div>
            </div>

            {/* List */}
            <div className="dash-topClientsList">
              {(!topClientsActive || topClientsActive.length === 0) ? (
                <div className="dash-topClientsEmpty">
                  {loading ? t("labels.loading") : t("labels.noData")}
                </div>
              ) : (
                topClientsActive.slice(0, 5).map((c, idx) => {
                  const val = Number(c?.revenue || 0);
                  const pct = Math.max(2, Math.min(100, (val / topClientsMax) * 100));

                  return (
                    <div className="dash-topClientsRow" key={`${topClientsTab}-${c.clientId ?? idx}`}>
                      <div className="dash-topClientsRowTop">
                        <div className="dash-topClientsName">{c.name}</div>
                        <div className="dash-topClientsValue">{formatMoneyTopClients(val)}</div>
                      </div>

                      <div className="dash-topClientsTrack" aria-hidden="true">
                        <div
                          className={`dash-topClientsFill c${idx % 5}`}
                          style={{
                            width: `${pct}%`,
                            animationDelay: `${0.12 + idx * 0.08}s`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="dash-topClientsHint">{t("panels.topClientsHint")}</div>
          </div>
        </div>

        {/* Charts */}
        <div className="dashboard-chartsGrid">
          <div className="dashboard-panel dashboard-glass dash-pdCard">
            <div className="dash-pdHead dash-pdHeadRef">
              <div className="dash-pdEyebrow">{t("panels.pickupsVsDeliveries")}</div>
              <div className="dash-pdTitleRef">{t("panels.dailyActivity")}</div>
            </div>

            <StackedBars series={stacked14} t={t} locale={currentLocale} />

            <div className="dashboard-chartLegend dash-pdLegend">
              <span className="dashboard-legendD">{t("labels.deliveries")}</span>
              <span className="dashboard-legendP">{t("labels.pickups")}</span>
            </div>
          </div>

          <div className="dashboard-panel dashboard-glass dash-revTruckCard">
            <div className="dash-revTruckHead">
              <div className="dash-revTruckTitleRow">
                <Truck className="dash-revTruckIcon" />
                <div className="dash-revTruckTitle">
                  {t("panels.revenuePerTruck")}
                </div>
              </div>
            </div>

            <RevenuePerTruckBars rows={revenuePerTruck} loading={loading} t={t} locale={currentLocale} />
          </div>

          <div className="dashboard-panel dashboard-glass dash-complianceCard">
            <div className="dash-complianceHead">
              <div className="dash-complianceTitleRow">
                <ShieldCheck className="dash-complianceIcon" strokeWidth={2.1} />
                <div className="dash-complianceTitle">
                  {t("panels.compliance")}
                </div>
              </div>
            </div>

            <div className="dash-complianceTable">
              <div className="dash-complianceTableHead">
                <span>{t("table.item")}</span>
                <span className="dash-complianceDueHead">{t("table.due")}</span>
              </div>

              <div className="dash-complianceScroll">
                {complianceDueSoon.length === 0 ? (
                  <div className="dash-complianceEmpty">
                    {loading ? t("labels.loading") : t("labels.noData")}
                  </div>
                ) : (
                  complianceDueSoon.map((it, idx) => (
                    <div
                      className="dash-complianceRow"
                      key={`${it.type}-${it.id}-${it.field}-${idx}`}
                    >
                      <div className="dash-complianceLeft">
                        <div className="dash-complianceMain">
                          {t(it.typeKey || "complianceTypes.unknown")} • {it.label}
                        </div>
                        <div className="dash-complianceSub">
                          {t(it.fieldKey || "complianceFields.unknown")}
                        </div>
                      </div>

                      <div className={`dash-complianceDate ${it?.isExpired ? "isExpired" : ""}`}>
                        {it.date}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="dash-complianceFoot">
              {t("panels.complianceHintTimezone", {
                timezone: dashboardMeta?.timezone || "Europe/Lisbon",
              })}
            </div>
          </div>

          <div className="dashboard-panel dashboard-glass dash-topLoadsCard">
            <div className="dash-topLoadsHead">
              <div className="dash-topLoadsTitleRow">
                <Package className="dash-topLoadsIcon" strokeWidth={2.1} />
                <div className="dash-topLoadsTitle">
                  {t("panels.topClientsLoads")}
                </div>
              </div>
            </div>

            {(() => {
              const normType = (x) => String(x || "").toUpperCase();

              const loadsIndustries = topClientsLoads.filter(
                (c) => normType(c.clientType) === "INDUSTRY"
              );
              const loadsCarriers = topClientsLoads.filter(
                (c) => normType(c.clientType) === "CARRIER"
              );

              const hasTypes = loadsIndustries.length > 0 || loadsCarriers.length > 0;
              const fallbackAll = !hasTypes ? topClientsLoads : [];

              const leftRows = hasTypes ? loadsIndustries.slice(0, 5) : fallbackAll.slice(0, 5);
              const rightRows = hasTypes ? loadsCarriers.slice(0, 5) : [];

              const Row = ({ c, side }) => (
                <div className="dash-topLoadsRow" key={`${side}-${c.clientId}`}>
                  <div className="dash-topLoadsName">{c.name}</div>
                  <div className="dash-topLoadsValue">
                    {formatCount(c.loads ?? 0, currentLocale)}
                  </div>
                </div>
              );

              return (
                <div className="dash-topLoadsGrid">
                  <div className="dash-topLoadsCol">
                    <div className="dash-topLoadsColHead">
                      <Building2 className="dash-topLoadsColIcon" strokeWidth={2.1} />
                      <span>
                        {hasTypes ? t("labels.industry") : t("table.client")}
                      </span>
                    </div>

                    {leftRows.length === 0 ? (
                      <div className="dash-topLoadsEmpty">
                        {loading ? t("labels.loading") : t("labels.noData")}
                      </div>
                    ) : (
                      leftRows.map((c) => <Row c={c} side="left" key={`left-${c.clientId}`} />)
                    )}
                  </div>

                  {hasTypes && (
                    <div className="dash-topLoadsCol">
                      <div className="dash-topLoadsColHead">
                        <Truck className="dash-topLoadsColIcon" strokeWidth={2.1} />
                        <span>{t("labels.carrier")}</span>
                      </div>

                      {rightRows.length === 0 ? (
                        <div className="dash-topLoadsEmpty">
                          {loading ? t("labels.loading") : t("labels.noData")}
                        </div>
                      ) : (
                        rightRows.map((c) => <Row c={c} side="right" key={`right-${c.clientId}`} />)
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="dash-topLoadsFoot">
              {t("panels.topClientsLoadsHint", { from, to })}
            </div>

            {topClientsLoadsMissingCount > 0 ? (
              <div className="dash-topLoadsWarning">
                {t("panels.topClientsLoadsDataQuality", {
                  count: topClientsLoadsMissingCount,
                })}
              </div>
            ) : null}
          </div>

          <div className="dashboard-panel dashboard-glass dash-actionsCard">
            <div className="dash-actionsHead">
              <span className="dash-actionsHeadIcon" aria-hidden="true">
                <AlertCircle size={17} />
              </span>
              <div className="dash-actionsHeadTitle">{t("panels.actionNow")}</div>
            </div>

            <div className="dashboard-actionList">
              <div
                className={`dashboard-actionItem ${(k.pendingPointsNow ?? 0) > 5 ? "isWarn" : ""}`}
              >
                <div className="dashboard-actionLeft">
                  <div className="dashboard-actionName">{t("actions.pendingPoints")}</div>
                  <div className="dashboard-actionSub">{t("actions.pendingPointsSub")}</div>
                </div>

                <div className="dashboard-actionRightWrap">
                  <div className={`dashboard-actionRight ${(k.pendingPointsNow ?? 0) > 5 ? "isWarn" : ""}`}>
                    {formatCount(k.pendingPointsNow ?? 0, currentLocale)}
                  </div>
                  <div className="dashboard-actionChevron">›</div>
                </div>
              </div>

              <div
                className={`dashboard-actionItem ${(anyUnassigned ?? 0) > 0 ? "isWarn" : ""}`}
              >
                <div className="dashboard-actionLeft">
                  <div className="dashboard-actionName">{t("actions.unassignedAny")}</div>
                  <div className="dashboard-actionSub">{t("actions.unassignedAnySub")}</div>
                  {(anyUnassigned ?? 0) > 0 ? (
                    <div className="dashboard-actionExtra">
                      {t("labels.truck")}: {formatCount(unassigned.truck ?? 0, currentLocale)} · {t("labels.driver")}: {formatCount(unassigned.driver ?? 0, currentLocale)} · {t("labels.trailer")}: {formatCount(unassigned.trailer ?? 0, currentLocale)}
                    </div>
                  ) : null}
                </div>

                <div className="dashboard-actionRightWrap">
                  <div className={`dashboard-actionRight ${(anyUnassigned ?? 0) > 0 ? "isWarn" : ""}`}>
                    {formatCount(anyUnassigned ?? 0, currentLocale)}
                  </div>
                  <div className="dashboard-actionChevron">›</div>
                </div>
              </div>

              <div
                className={`dashboard-actionItem ${(k.latePointsInRange ?? 0) > 0 ? "isCritical" : ""}`}
              >
                <div className="dashboard-actionLeft">
                  <div className="dashboard-actionName">{t("actions.latePoints")}</div>
                  <div className="dashboard-actionSub">{t("actions.latePointsSubInRange")}</div>
                </div>

                <div className="dashboard-actionRightWrap">
                  <div className={`dashboard-actionRight ${(k.latePointsInRange ?? 0) > 0 ? "isCritical" : ""}`}>
                    {formatCount(k.latePointsInRange ?? 0, currentLocale)}
                  </div>
                  <div className="dashboard-actionChevron">›</div>
                </div>
              </div>

              <div className="dashboard-actionItem">
                <div className="dashboard-actionLeft">
                  <div className="dashboard-actionName">{t("actions.openInvoices")}</div>
                  <div className="dashboard-actionSub">{t("actions.openInvoicesSub")}</div>
                </div>

                <div className="dashboard-actionRightWrap">
                  <div className="dashboard-actionRight">€{formatMoney(k.invoicesOpenTotal, currentLocale)}</div>
                  <div className="dashboard-actionChevron">›</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-panel dashboard-glass dash-statusCard">
            <div className="dash-statusHead dash-statusHeadRef">
              <div className="dash-statusEyebrow">{t("panels.statusLoadsEyebrow")}</div>
              <div className="dash-statusTitleRow">
                <div className="dash-statusTitle">{t("panels.statusBreakdown")}</div>
              </div>
            </div>

            <DonutChart items={statusItems} t={t} locale={currentLocale} />
          </div>
        </div>

        {/* ✅ Bottom mini cards row (separado, igual imagem 1) */}
        <div className="dash-bottomMiniRow">
          <div className="dash-miniCards dash-miniCardsBottom">
            <div className="dash-miniCard isBlue">
              <div className="dash-miniIconBox">
                <Truck size={22} />
              </div>
              <div className="dash-miniText">
                <div className="dash-miniValue">
                  {counts.trucks != null ? formatCount(counts.trucks, currentLocale) : "-"}
                </div>
                <div className="dash-miniLabel">{t("counts.trucks")}</div>
              </div>
            </div>

            <div className="dash-miniCard isPurple">
              <div className="dash-miniIconBox">
                <Container size={22} />
              </div>
              <div className="dash-miniText">
                <div className="dash-miniValue">
                  {counts.trailers != null ? formatCount(counts.trailers, currentLocale) : "-"}
                </div>
                <div className="dash-miniLabel">{t("counts.trailers")}</div>
              </div>
            </div>

            <div className="dash-miniCard isPink">
              <div className="dash-miniIconBox">
                <Users size={22} />
              </div>
              <div className="dash-miniText">
                <div className="dash-miniValue">
                  {counts.drivers != null ? formatCount(counts.drivers, currentLocale) : "-"}
                </div>
                <div className="dash-miniLabel">{t("counts.drivers")}</div>
              </div>
            </div>

            <div className="dash-miniCard isGreen">
              <div className="dash-miniIconBox">
                <Building2 size={22} />
              </div>
              <div className="dash-miniText">
                <div className="dash-miniValue">
                  {counts.clients != null ? formatCount(counts.clients, currentLocale) : "-"}
                </div>
                <div className="dash-miniLabel">{t("counts.clients")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}